import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Input from '@/components/Input/Input';
import Button from '@/components/new-design/Button';
import ErrorText from '@/components/ErrorText';
import ROUTES from '@/routers/Routes';
import { useDispatch, useSelector } from 'react-redux';
import { ReducerState } from '@/services/common/lib/reducerState';
import { validationSchema } from '../validations/register.validation';
import { AuthEvents, authActionCreator } from '@/services/auth/authActions';
import { Wallet, usePrivy, useWallets } from '@privy-io/react-auth';
import { UserCreatePrivy } from '@/features/auth/interfaces/privy.interface';
import { getReferralCode } from '@/features/auth/utils/referral-code';
import { IoIosArrowRoundBack } from 'react-icons/io';
import HelperText from '@/components/HelperText';

interface FormInputs {
  alias: string;
  firstName: string;
  lastName: string;
  phone?: number;
  phoneCode: string;
  referralCode?: string | undefined;
}

const RegisterForm = () => {
  const dispatch = useDispatch();
  const referrerCode = getReferralCode();
  const registerError = useSelector((state: ReducerState) => state.auth.registerError);
  const isLoading = useSelector((state: ReducerState) => !!state.auth.loading);

  const { createWallet, authenticated, user: privyUser, logout } = usePrivy();
  const { wallets } = useWallets();
  // What is this used for?
  const [_isWalletConnected, setIsWalletConnected] = useState<boolean>(false);
  const [isWalletLinked, setIsWalletLinked] = useState<boolean>(false);
  const [authError, setAuthError] = useState<string>('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInputs>({
    resolver: yupResolver(validationSchema),
    mode: 'onBlur',
    defaultValues: {
      phoneCode: 'US',
      phone: undefined,
      referralCode: referrerCode || undefined,
    },
  });

  const onSubmit = async ({
    alias,
    //phone, phoneCode,
    firstName,
    lastName,
    referralCode,
  }: FormInputs) => {
    try {
      setIsSubmitted(true);
      let walletAddress: Wallet | undefined;

      if (!isWalletLinked) {
        walletAddress = await createWallet();
        setIsWalletLinked(true);
      }

      if (alias.length > 15) {
        throw new Error("Screen Name can't have more than 15 characters");
      }

      if (!walletAddress && !isWalletLinked) {
        throw new Error('Wallet address could not be created');
      }

      if (privyUser) {
        const newUser: UserCreatePrivy = {
          privyId: privyUser.id,
          email: privyUser.email?.address || privyUser.google?.email || '',
          referralCode,
          alias,
          firstName,
          lastName,
        };

        dispatch(authActionCreator(AuthEvents.REGISTER_USER, newUser));
      } else {
        if (authenticated) await logout();
      }
    } catch (error) {
      if (isAxiosError(error) && error.response?.data.message)
        return setAuthError(error.response?.data.message || 'Something went wrong');

      if (error instanceof Error) return setAuthError(error.message);

      setAuthError('Something went wrong');
      setIsSubmitted(false);
    } finally {
      setIsSubmitted(false);
    }
  };

  useEffect(() => {
    setIsWalletConnected(wallets.length > 0);
    if (wallets.length > 0 && wallets[0].linked) {
      setIsWalletLinked(true);
    }
  }, [wallets]);

  const isRegistered = useSelector((state: ReducerState) => !!state.auth.alias);

  const AliasMaxLength = 15;

  const handleGoBack = async () => {
    if (authenticated) await logout();
  };

  return authenticated && isRegistered ? (
    <Navigate to={ROUTES.LOBBY} />
  ) : (
    <div className="scrollbar-phenom z-10 mx-auto flex h-full w-full max-w-[500px] flex-col overflow-auto py-8 md:min-w-96">
      <div className="z-50 mb-10 flex w-full select-none items-center justify-between">
        <IoIosArrowRoundBack
          size={34}
          className="text-lg font-bold text-ph-gray-silver hover:cursor-pointer hover:text-ph-gray-white"
          onClick={handleGoBack}
        />
        <h1 className="text-center text-2xl font-semibold text-ph-gray-white">Complete Registration</h1>

        <div className="w-8" />
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-8">
        <div className="flex flex-col gap-1">
          <Input
            maxLength={AliasMaxLength}
            type="text"
            placeholder="Screen Name"
            {...register('alias')}
            fullWidth
            error={!!errors.alias}
            autoComplete="off"
          />
          {errors.alias?.message ? (
            <ErrorText className="ml-2">* {errors.alias.message}</ErrorText>
          ) : (
            <HelperText className="ml-2">Screen name cannot be changed later</HelperText>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <Input
            type="text"
            placeholder="First Name"
            {...register('firstName')}
            fullWidth
            error={!!errors.firstName}
            autoComplete="off"
          />
          {errors.firstName?.message && <ErrorText className="ml-2">* {errors.firstName.message}</ErrorText>}
        </div>

        <div className="flex flex-col gap-1">
          <Input
            type="text"
            placeholder="Last Name"
            {...register('lastName')}
            fullWidth
            error={!!errors.lastName}
            autoComplete="off"
          />
          {errors.lastName?.message && <ErrorText className="ml-2">* {errors.lastName.message}</ErrorText>}
        </div>

        <div className="flex flex-col gap-1">
          <Input
            maxLength={10}
            type="text"
            placeholder="Referral Code"
            {...register('referralCode')}
            error={!!errors.referralCode}
            fullWidth
            autoComplete="off"
          />
          <HelperText className="ml-2">Did someone refer you?</HelperText>
        </div>

        <div className="my-3 flex flex-col gap-5">
          <Button fullWidth type="submit" disabled={isSubmitted} isLoading={isSubmitted || isLoading}>
            Complete
          </Button>

          {(authError || registerError) && (
            <div className="w-full text-center">
              <ErrorText>{authError || registerError}</ErrorText>
            </div>
          )}
        </div>
      </form>
    </div>
  );
};

export default RegisterForm;
